// Renderer selection and the accessors the map draws through.
//
// Two backends implement the same contract: a layer skeleton whose nodes accept
// the chained `append`/`attr`/`text` calls the schemas already make, a viewport,
// a per-frame `draw`, and a `measure` standing in for getBBox. WebGL is the
// default; SVG remains as the fallback and as the reference for what the map is
// supposed to look like.
//
// Everything that used to reach for `document.querySelector("defs")` or
// `"#overlay"` goes through the accessors here instead, because under WebGL those
// layers are scene nodes rather than anything in the document.

import { WebGLRenderer } from "./webgl/renderer";
import { SvgRenderer } from "./svg/renderer";
import { onEffectsChanged } from "./effects";

export const RENDERER_WEBGL = "webgl";
export const RENDERER_SVG = "svg";

const STORAGE_KEY = "borgRenderer";

let active = null;

function isValid(kind) {
  return kind === RENDERER_SVG || kind === RENDERER_WEBGL;
}

// Stored in localStorage so the choice outlives the tab, and mirrored into
// sessionStorage so it still applies if the former is unavailable. The URL wins
// over either but cannot be relied on alone: signing in bounces through Azure AD
// and comes back without the query string.
export function preferredRenderer() {
  try {
    const fromUrl = new URLSearchParams(window.location.search).get("renderer");
    if (isValid(fromUrl)) {
      store(fromUrl);
      return fromUrl;
    }

    const persisted = window.localStorage.getItem(STORAGE_KEY);
    if (isValid(persisted)) return persisted;

    const perTab = window.sessionStorage.getItem(STORAGE_KEY);
    if (isValid(perTab)) return perTab;
  } catch (e) {
    // Private browsing or storage disabled.
  }
  return RENDERER_WEBGL;
}

function store(kind) {
  try {
    window.localStorage.setItem(STORAGE_KEY, kind);
  } catch (e) {
    // As below.
  }
  try {
    window.sessionStorage.setItem(STORAGE_KEY, kind);
  } catch (e) {
    // Nothing useful to do; the reload will keep the current renderer.
  }
}

// Swapping backends means rebuilding every object's drawing from scratch, along
// with the meters, page-switch animations and callout tracks their `enter`
// handlers set up. Reloading is the honest way to get that, and the websocket
// replays the whole estate on connect anyway.
export function setPreferredRenderer(kind) {
  store(kind);
  window.location.reload();
}

export function createRenderer(container) {
  const wanted = preferredRenderer();

  if (wanted === RENDERER_WEBGL) {
    try {
      active = new WebGLRenderer(container);
      return active;
    } catch (e) {
      console.warn("[borg-map] falling back to the SVG renderer:", e);
      // A renderer that failed part-way through construction may have left its
      // drawing surface behind; the container belongs entirely to the renderer,
      // so clearing it is the reliable way to start again.
      while (container.firstChild) container.removeChild(container.firstChild);
    }
  }

  active = new SvgRenderer(container);
  return active;
}

if (typeof window !== "undefined") {
  // Alongside window.map, window.camera and window.perf, so a session can be
  // inspected from the console.
  Object.defineProperty(window, "mapRenderer", { get: () => active });
}

// An effect that changes how geometry is built has to throw away what is already
// built. The SVG backend has none of these, so it has nothing to rebuild.
onEffectsChanged(() => {
  if (active && active.markAllDirty) active.markAllDirty();
});

export function renderer() {
  return active;
}

export function rendererKind() {
  return active ? active.kind : preferredRenderer();
}

// ---------------------------------------------------------------------------
// Layer accessors
// ---------------------------------------------------------------------------

export function mapRoot() {
  return active.root;
}

export function mapDefs() {
  return active.defs;
}

export function mapUnderlay() {
  return active.underlay;
}

export function mapOverlay() {
  return active.overlay;
}

export function mapMain() {
  return active.main;
}

export function mapLines() {
  return active.lines;
}

export function mapLineGroup() {
  return active.lineGroup;
}

export function mapStatusText() {
  return active.statusText;
}

// Draws the frame. A no-op under SVG, where the browser owns painting.
export function drawFrame() {
  if (active) active.draw();
}
